type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;
type UmamiPayload = Record<string, unknown>;

interface UmamiTracker {
  track: {
    (eventName: string, data?: UmamiPayload): void;
    (payload: (props: UmamiPayload) => UmamiPayload): void;
  };
}

declare global {
  interface Window {
    umami?: UmamiTracker;
  }
}

export type AnalyticsConsent = 'granted' | 'denied' | 'unset';
export const ANALYTICS_CONSENT_KEY = 'modelmap:analytics-consent';

// Self-hosted Umami. The tracker and collection endpoint use custom paths
// (/cf-analytics.js, /api/cf-collect) configured on the Umami server.
export const UMAMI_SCRIPT_URL =
  import.meta.env.VITE_UMAMI_SCRIPT_URL || 'https://analytics.hannah-co.com/cf-analytics.js';
const UMAMI_WEBSITE_ID = import.meta.env.VITE_UMAMI_WEBSITE_ID ?? '';
const UMAMI_DOMAINS = import.meta.env.VITE_UMAMI_DOMAINS || 'modelmap.app';
const MAX_QUEUED_CALLS = 50;

type QueuedCall = (tracker: UmamiTracker) => void;
let pendingCalls: QueuedCall[] = [];
let scriptRequested = false;

export function isAnalyticsConfigured(): boolean {
  return UMAMI_WEBSITE_ID.trim().length > 0;
}

export function getAnalyticsConsent(): AnalyticsConsent {
  if (typeof window === 'undefined') return 'unset';
  if (window.navigator.doNotTrack === '1') return 'denied';
  const stored = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
  return stored === 'granted' || stored === 'denied' ? stored : 'unset';
}

export function setAnalyticsConsent(consent: Exclude<AnalyticsConsent, 'unset'>): void {
  window.localStorage.setItem(ANALYTICS_CONSENT_KEY, consent);
}

function analyticsAllowed(): boolean {
  if (typeof window === 'undefined' || !isAnalyticsConfigured()) return false;
  if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEV_ANALYTICS !== 'true') return false;
  return getAnalyticsConsent() === 'granted';
}

function flushPendingCalls(): void {
  const tracker = window.umami;
  if (!tracker) return;
  const calls = pendingCalls;
  pendingCalls = [];
  calls.forEach((call) => call(tracker));
}

export function initialiseAnalytics(): void {
  if (!analyticsAllowed() || scriptRequested || window.umami) return;
  scriptRequested = true;

  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.src = UMAMI_SCRIPT_URL;
  script.dataset.websiteId = UMAMI_WEBSITE_ID;
  script.dataset.domains = UMAMI_DOMAINS;
  // Page views are sent explicitly on route change by trackPageView.
  script.dataset.autoTrack = 'false';
  script.dataset.doNotTrack = 'true';
  script.dataset.excludeHash = 'true';
  script.dataset.modelmapAnalytics = 'true';
  // Assigned from script (not an inline attribute), so the CSP allows it.
  script.addEventListener('load', flushPendingCalls);
  document.head.appendChild(script);
}

function dispatch(call: QueuedCall): void {
  if (!analyticsAllowed()) return;
  if (window.umami) {
    call(window.umami);
    return;
  }
  if (!scriptRequested) return;
  if (pendingCalls.length < MAX_QUEUED_CALLS) pendingCalls.push(call);
}

export function trackPageView(path: string): void {
  dispatch((tracker) =>
    tracker.track((props) => ({ ...props, url: path, title: document.title }))
  );
}

export function trackEvent(eventName: string, params: AnalyticsParams = {}): void {
  const data = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  );
  // Umami caps event names at 50 characters.
  dispatch((tracker) => tracker.track(eventName.slice(0, 50), data));
}

/** Test-only reset of module state. */
export function resetAnalyticsForTests(): void {
  pendingCalls = [];
  scriptRequested = false;
}
